"use client";

import { getCookie } from "cookies-next/client";
import posthog from "posthog-js";
import type { BootstrapConfig } from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import type { ComponentProps } from "react";
import { useEffect, useRef } from "react";

import { readCampaignParams } from "@/components/posthog/campaign-params";
import type { CampaignParams } from "@/components/posthog/campaign-params";
import {
  createGeoBeforeSend,
  fetchClientGeo,
} from "@/components/posthog/geo-before-send";
import { captureLandingPageview } from "@/components/posthog/landing-pageview";
import {
  applyPerformanceConsent,
  hasPublishedConsentDecision,
  isPerformanceConsentGiven,
} from "@/components/posthog/performance-consent";
import { useOneTrustLoaded } from "@/hooks/use-onetrust-loaded";
import {
  POSTHOG_API_HOST,
  POSTHOG_BOOTSTRAP_DATA_COOKIE,
  POSTHOG_KEY,
  POSTHOG_UI_HOST,
} from "@/lib/constants";
import logging from "@/lib/logging";
import { hasNativeWebMCPSupport } from "@/lib/webmcp/runtime";
import { posthogBootstrapDataSchema } from "@/schemas";
import type { VercelGeo } from "@/schemas";

const moduleLogger = logging.child({
  module: "posthog/posthog-provider",
});

declare global {
  interface Window {
    // OneTrust's comma-wrapped list of active categories, e.g. ",C0001,C0002,"
    OnetrustActiveGroups?: string;
  }
}

const bootstrapDataSchema = posthogBootstrapDataSchema;

/** Super properties stamped on every event from this app. */
const SUPER_PROPERTIES = {
  event_source: "website",
  webmcp_native_support: hasNativeWebMCPSupport(),
};

export function PHProvider({
  children,
}: Omit<ComponentProps<typeof PostHogProvider>, "client">) {
  const oneTrustLoaded = useOneTrustLoaded();
  // Geo lives in memory only — never in cookies or browser storage.
  const geoRef = useRef<VercelGeo | null>(null);
  const geoLookupRef = useRef<Promise<VercelGeo | null> | null>(null);
  const landingCapturedRef = useRef(false);
  // Bumped on every consent transition. A pending pageview capture carries the
  // epoch it was started under and abandons itself if that has moved on, so a
  // capture can never land against an identity it was not started for.
  const consentEpochRef = useRef(0);
  // Snapshot of the landing URL's campaign params. Consent can be granted long
  // after an SPA navigation has dropped the UTMs from the URL, so they are read
  // once at init and replayed onto each new identity.
  const campaignParamsRef = useRef<CampaignParams | null>(null);

  // Cookieless events have their IP stripped before PostHog's GeoIP
  // enrichment runs, so geo is derived server-side from Vercel headers and
  // attached client-side via before_send. Idempotent: the first call starts
  // the lookup, later calls reuse it. Never blocks init — that would drop
  // every event fired in the window (posthog-js has no pre-init queue).
  // fetchClientGeo never rejects (any failure resolves to null).
  const startGeoLookup = () => {
    geoLookupRef.current ??= fetchClientGeo().then((geo) => {
      geoRef.current = geo;
      return geo;
    });
    return geoLookupRef.current;
  };

  // Captures the one $pageview owed to whichever identity is current, tagged
  // with the consent epoch so a later transition invalidates it rather than
  // letting it fire against the wrong identity.
  const capturePageviewForCurrentIdentity = () => {
    const epoch = consentEpochRef.current;
    const granted = posthog.get_explicit_consent_status() === "granted";
    return captureLandingPageview(posthog, {
      geoPromise: granted ? null : startGeoLookup(),
      shouldCapture: () => consentEpochRef.current === epoch,
    });
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      campaignParamsRef.current ??= readCampaignParams();

      const bootstrapDataCookie = getCookie(POSTHOG_BOOTSTRAP_DATA_COOKIE);
      let bootstrapData: BootstrapConfig | undefined;
      if (bootstrapDataCookie) {
        try {
          bootstrapData = bootstrapDataSchema.parse(
            JSON.parse(bootstrapDataCookie)
          );
        } catch (error) {
          moduleLogger.error({ error }, "Error parsing bootstrap data");
        }
      }

      // https://posthog.com/docs/libraries/js#persistence
      // cookieless_mode: 'on_reject' handles consent transitions properly:
      // - opt_in_capturing() creates session manager, recording infra, and new distinct ID
      // - opt_out_capturing() switches to cookieless server-side hash tracking
      // - SDK remembers consent decision across page loads automatically
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_API_HOST,
        ui_host: POSTHOG_UI_HOST,
        person_profiles: "identified_only",
        // Suppresses only the SDK's init-time initial pageview: the landing
        // pageview is captured manually (captureLandingPageview) after a
        // bounded wait for geo, while history-change capture is enabled
        // immediately. Init itself stays synchronous.
        capture_pageview: false,
        capture_pageleave: true,
        persistence: "localStorage+cookie",
        cross_subdomain_cookie: true,
        cookieless_mode: "on_reject",
        opt_out_capturing_persistence_type: "cookie",
        autocapture: {
          element_allowlist: ["a", "button"],
        },
        bootstrap: bootstrapData,
        before_send: createGeoBeforeSend(() => geoRef.current),
      });

      // Registered before consent is resolved so it is present on every event,
      // and again inside applyPerformanceConsent because a consent transition
      // resets persistence and would otherwise drop it.
      posthog.register_once(SUPER_PROPERTIES);

      // Resolve consent before anything is captured. OneTrust's global is
      // normally populated before hydration, and trusting only PostHog's
      // persisted decision would let one cookie-backed pageview escape for a
      // visitor who has since withdrawn performance consent.
      const publishedGroups = window.OnetrustActiveGroups;
      if (hasPublishedConsentDecision(publishedGroups)) {
        applyPerformanceConsent(
          posthog,
          isPerformanceConsentGiven(publishedGroups),
          {
            campaignParams: campaignParamsRef.current ?? {},
            superProperties: SUPER_PROPERTIES,
          }
        );
      } else if (posthog.get_explicit_consent_status() === "pending") {
        // No decision published yet, so start capturing in cookieless mode
        // rather than losing a fast bounce: on_reject mode captures nothing
        // until consent is explicit either way.
        posthog.opt_out_capturing();
      }

      // History-change tracking starts immediately (so no SPA navigation is
      // missed); only the landing pageview waits. Cookieless (and
      // pending-consent) sessions wait on the geo lookup so it carries geo;
      // consented sessions capture immediately and keep PostHog's
      // server-side GeoIP enrichment. The ref guards strict-mode double
      // effects from double-capturing.
      if (!landingCapturedRef.current) {
        landingCapturedRef.current = true;
        void capturePageviewForCurrentIdentity();
      }
    }
  }, []);

  useEffect(() => {
    const applyConsent = (activeGroups?: string | string[] | null) => {
      const granted = isPerformanceConsentGiven(activeGroups);
      // Covers a mid-session consent withdrawal on a session that loaded with
      // consent granted (where the init-time lookup was skipped), so the
      // now-cookieless events still get geo. No-op if geo is already in memory
      // or the lookup already ran.
      if (!granted && !geoRef.current) {
        void startGeoLookup();
      }
      if (
        !applyPerformanceConsent(posthog, granted, {
          campaignParams: campaignParamsRef.current ?? {},
          superProperties: SUPER_PROPERTIES,
        })
      ) {
        return;
      }
      // The transition reset identity, so the new person owns its own pageview.
      // Bumping the epoch first invalidates any capture still waiting on geo for
      // the previous identity, so each identity gets exactly one — chaining onto
      // the pending capture instead would drop the pageview for an identity that
      // is superseded before that capture resolves.
      consentEpochRef.current += 1;
      void capturePageviewForCurrentIdentity();
    };

    const handler: EventListener = (event) => {
      // OneTrustGroupsUpdated fires when consent becomes available on page load
      // and whenever consent is changed
      // https://my.onetrust.com/s/article/UUID-518074a1-a6da-81c3-be52-bae7685d9c94
      applyConsent((event as CustomEvent<string | string[]>).detail);
    };

    if (oneTrustLoaded) {
      window.addEventListener("OneTrustGroupsUpdated", handler);
      // OneTrustGroupsUpdated fires once, early — often before this listener is
      // attached, because useOneTrustLoaded polls on a 1s interval. Reconcile
      // from the global so a missed event cannot strand the page in the wrong
      // state: a missed grant leaves a consented visitor cookieless, and a
      // missed withdrawal keeps tracking someone who has opted out. Gated on
      // OneTrust having published a decision so the pre-decision window is not
      // mistaken for a denial.
      if (hasPublishedConsentDecision(window.OnetrustActiveGroups)) {
        applyConsent(window.OnetrustActiveGroups);
      }
    }
    return () => {
      window.removeEventListener("OneTrustGroupsUpdated", handler);
    };
  }, [oneTrustLoaded]);

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}
